// Offline Storage Service using IndexedDB
const DB_NAME = 'bourbon_offline';
const DB_VERSION = 1;

let db = null;

// Initialize IndexedDB
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[OfflineDB] Failed to open database');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('[OfflineDB] Database opened successfully');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Pending posts store (posts created offline)
      if (!database.objectStoreNames.contains('pending_posts')) {
        const postsStore = database.createObjectStore('pending_posts', { keyPath: 'local_id' });
        postsStore.createIndex('created_at', 'created_at', { unique: false });
        postsStore.createIndex('status', 'status', { unique: false });
      }

      // Cached posts store (for offline reading)
      if (!database.objectStoreNames.contains('cached_posts')) {
        const cachedStore = database.createObjectStore('cached_posts', { keyPath: 'post_id' });
        cachedStore.createIndex('commune', 'commune', { unique: false });
        cachedStore.createIndex('cached_at', 'cached_at', { unique: false });
      }

      // Pending actions (likes, comments, etc.)
      if (!database.objectStoreNames.contains('pending_actions')) {
        const actionsStore = database.createObjectStore('pending_actions', { keyPath: 'action_id', autoIncrement: true });
        actionsStore.createIndex('type', 'type', { unique: false });
        actionsStore.createIndex('created_at', 'created_at', { unique: false });
      }

      console.log('[OfflineDB] Database schema created/updated');
    };
  });
};

// Save a post for offline sync
export const savePendingPost = async (postData) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_posts'], 'readwrite');
    const store = transaction.objectStore('pending_posts');

    const pendingPost = {
      local_id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...postData,
      status: 'pending',
      created_at: new Date().toISOString(),
      retry_count: 0
    };

    const request = store.add(pendingPost);

    request.onsuccess = () => {
      console.log('[OfflineDB] Post saved for later sync');
      resolve(pendingPost);
    };

    request.onerror = () => {
      console.error('[OfflineDB] Failed to save pending post');
      reject(request.error);
    };
  });
};

// Get all pending posts
export const getPendingPosts = async () => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_posts'], 'readonly');
    const store = transaction.objectStore('pending_posts');
    const index = store.index('status');
    const request = index.getAll('pending');

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Remove a synced post
export const removePendingPost = async (localId) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_posts'], 'readwrite');
    const store = transaction.objectStore('pending_posts');
    const request = store.delete(localId);

    request.onsuccess = () => {
      console.log('[OfflineDB] Pending post removed');
      resolve();
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Update pending post status
export const updatePendingPostStatus = async (localId, status, error = null) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_posts'], 'readwrite');
    const store = transaction.objectStore('pending_posts');
    const getRequest = store.get(localId);

    getRequest.onsuccess = () => {
      const post = getRequest.result;
      if (post) {
        post.status = status;
        post.last_error = error;
        post.retry_count = (post.retry_count || 0) + 1;
        post.last_attempt = new Date().toISOString();
        
        const updateRequest = store.put(post);
        updateRequest.onsuccess = () => resolve(post);
        updateRequest.onerror = () => reject(updateRequest.error);
      } else {
        reject(new Error('Post not found'));
      }
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

// Cache posts for offline reading
export const cachePosts = async (posts) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['cached_posts'], 'readwrite');
    const store = transaction.objectStore('cached_posts');

    const now = new Date().toISOString();
    let completed = 0;

    posts.forEach((post) => {
      const cachedPost = {
        ...post,
        cached_at: now
      };
      
      const request = store.put(cachedPost);
      request.onsuccess = () => {
        completed++;
        if (completed === posts.length) {
          console.log(`[OfflineDB] Cached ${posts.length} posts`);
          resolve();
        }
      };
      request.onerror = () => {
        console.error('[OfflineDB] Failed to cache post');
      };
    });

    if (posts.length === 0) {
      resolve();
    }
  });
};

// Get cached posts
export const getCachedPosts = async (commune = null) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['cached_posts'], 'readonly');
    const store = transaction.objectStore('cached_posts');
    
    let request;
    if (commune) {
      const index = store.index('commune');
      request = index.getAll(commune);
    } else {
      request = store.getAll();
    }

    request.onsuccess = () => {
      const posts = request.result || [];
      // Sort by created_at descending
      posts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      resolve(posts);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Save pending action (like, comment, etc.)
export const savePendingAction = async (type, data) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_actions'], 'readwrite');
    const store = transaction.objectStore('pending_actions');

    const action = {
      type,
      data,
      created_at: new Date().toISOString(),
      status: 'pending'
    };

    const request = store.add(action);

    request.onsuccess = () => {
      console.log(`[OfflineDB] Saved pending ${type} action`);
      resolve({ ...action, action_id: request.result });
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Get all pending actions
export const getPendingActions = async () => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_actions'], 'readonly');
    const store = transaction.objectStore('pending_actions');
    const request = store.getAll();

    request.onsuccess = () => {
      resolve(request.result || []);
    };

    request.onerror = () => {
      reject(request.error);
    };
  });
};

// Remove synced action
export const removePendingAction = async (actionId) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['pending_actions'], 'readwrite');
    const store = transaction.objectStore('pending_actions');
    const request = store.delete(actionId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Clear old cached posts (older than 7 days)
export const clearOldCache = async (maxAgeDays = 7) => {
  const database = await initOfflineDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(['cached_posts'], 'readwrite');
    const store = transaction.objectStore('cached_posts');
    const index = store.index('cached_at');
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - maxAgeDays);
    
    const range = IDBKeyRange.upperBound(cutoffDate.toISOString());
    const request = index.openCursor(range);
    
    let deletedCount = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        deletedCount++;
        cursor.continue();
      } else {
        console.log(`[OfflineDB] Cleared ${deletedCount} old cached posts`);
        resolve(deletedCount);
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// Check if we're online
export const isOnline = () => navigator.onLine;

// Initialize offline storage
export const initOfflineStorage = async () => {
  await initOfflineDB();
  
  // Clear old cache on startup
  try {
    await clearOldCache();
  } catch (error) {
    console.error('[OfflineDB] Failed to clear old cache:', error);
  }
};

export default {
  initOfflineDB,
  initOfflineStorage,
  savePendingPost,
  getPendingPosts,
  removePendingPost,
  updatePendingPostStatus,
  cachePosts,
  getCachedPosts,
  savePendingAction,
  getPendingActions,
  removePendingAction,
  clearOldCache,
  isOnline
};
